import axios from "axios";
import { ENDPOINT_LOGIN, ENDPOINT_REFRESH_TOKEN } from "./auth.api";
import { tokenStore } from "store/token.store";

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Track ongoing token refresh to prevent race conditions
let refreshTokenPromise: Promise<string> | null = null;

// Request interceptor to add Bearer token
instance.interceptors.request.use(
  (config) => {
    const token = tokenStore.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

instance.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalConfig = err.config;
    if (isUnauthorized(err)) {
      if (isRefreshTokenRequest(err)) {
        // Refresh token expired, user needs to log in again
        refreshTokenPromise = null;
        tokenStore.clearTokens();
        window.location.href = window.location.origin + "/signin";
        return Promise.reject(err);
      } else if (isLoginRequest(err)) {
        return Promise.reject(err);
      }
      originalConfig._retry = true;
      try {
        // If a refresh is already in progress, wait for it
        if (refreshTokenPromise) {
          const newAccessToken = await refreshTokenPromise;
          originalConfig.headers.Authorization = `Bearer ${newAccessToken}`;
          return instance(originalConfig);
        }

        // Start a new refresh token request
        refreshTokenPromise = (async () => {
          try {
            const refreshToken = tokenStore.getRefreshToken();
            if (!refreshToken) {
              tokenStore.clearTokens();
              window.location.href = window.location.origin + "/signin";
              throw new Error("No refresh token available");
            }

            // Use refresh token for this request
            const response = await axios.post(
              `${import.meta.env.VITE_API_URL}${ENDPOINT_REFRESH_TOKEN}`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${refreshToken}`,
                  "Content-Type": "application/json",
                },
              },
            );

            // Store new access token
            const { accessToken, accessTokenExpiresIn } = response.data;
            tokenStore.setAccessToken(accessToken, accessTokenExpiresIn);
            tokenStore.updateTokenSetTime();

            return accessToken;
          } finally {
            // Clear the promise so subsequent requests can refresh again if needed
            refreshTokenPromise = null;
          }
        })();

        const newAccessToken = await refreshTokenPromise;

        // Retry original request with new token
        originalConfig.headers.Authorization = `Bearer ${newAccessToken}`;
        return instance(originalConfig);
      } catch (_error) {
        refreshTokenPromise = null;
        tokenStore.clearTokens();
        window.location.href = window.location.origin + "/signin";
        return Promise.reject(_error);
      }
    }
    return Promise.reject(err);
  },
);
export default instance;

const isUnauthorized = (err: any) =>
  err.response && err.response.status === 401 && !err.config._retry;

const isRefreshTokenRequest = (err: any) =>
  err.config.url === ENDPOINT_REFRESH_TOKEN;

const isLoginRequest = (err: any) => err.config.url === ENDPOINT_LOGIN;

export enum ApiUrl {
  WasteStation = "/waste-station",
  Waste = "/waste",
  WasteStationNotification = "/waste-station-notification",
  TransportSedel = "/transport-sedel",
  FeatureFlag = "/feature-flag",
}

export const getErrorMessage = (err: any) => {
  if (err.response?.data?.message) {
    return err.response.data.message;
  }
  if (err.response?.data?.errors) {
    return err.response.data.errors;
  }
  return "Något gick fel";
};

export const getAxiosErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      // Check if the error response contains data and a message
      if (
        error.response.data &&
        typeof error.response.data === "object" &&
        "message" in error.response.data
      ) {
        return (error.response.data as { message: string }).message;
      }
      return error.response.statusText;
    } else if (error.request) {
      return "No response received from the server";
    } else {
      return error.message;
    }
  } else if (error instanceof Error) {
    return error.message;
  } else {
    return "An unknown error occurred";
  }
};
